// src/assistant/components/FeedbackBar.jsx
import React, { useState, useContext } from "react";
import { MyContext } from '../../MyContext';
/**
 * Self-contained feedback bar.
 * Props:
 *  - question: string (the user's prompt that produced this answer)
 *  - answer: string (assistant's response text)
 *  - meta: object (optional) { tenantId, siteId, userId, sessionId, messageId, shape, mode, timing, payload_stats }
 *  - endpoint: string (optional) default "/assistant/feedback"
 *  - onSubmitted: fn(verdict) => void (optional) called after successful submit
 *  - disabled: boolean (optional) disables voting
 */
export default function FeedbackBar({
  question,
  answer,
  meta = {},
  endpoint = "https://api.mutcloud.com/ai-tools/feedback",
  onSubmitted,
  disabled = false,
}) {
  const { customerName } = useContext(MyContext)
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (verdict) => {
    if (disabled || sending || picked) return;
    setSending(true);
    setError("");

    const payload = {
      tenantId: customerName || "unknown",
      siteId: customerName || "unknown",
      userId: meta.userId || null,
      sessionId: meta.sessionId || null,
      messageId: meta.messageId || null,
      verdict, // "up" | "down"
      question: question || "",
      answer: answer || "",
      shape: meta.shape || null,
      mode: meta.mode || null,
      timing: meta.timing || null,
      payload_stats: meta.payload_stats || null,
      createdAt: new Date().toISOString(),
    };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPicked(verdict);
      if (onSubmitted) onSubmitted(verdict);
    } catch (e) {
      setError("Couldn’t send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const Btn = ({ label, value }) => (
    <button
      onClick={() => submit(value)}
      disabled={disabled || sending || !!picked}
      style={{
        marginRight: 8,
        padding: "4px 8px",
        borderRadius: 6,
        border: "1px solid #e5e7eb",
        background: picked === value ? "#e5f6ff" : "white",
        cursor: disabled || sending || picked ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      aria-label={label}
      title={label}
    >
      {label}
    </button>
  );

  return (
    <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
      {picked ? (
        <span>{picked === "up" ? "Thanks for the feedback! 👍" : "Got it — we’ll improve. 👌"}</span>
      ) : (
        <>
          Was this helpful? <Btn label="👍 Yes" value="up" /> <Btn label="👎 No" value="down" />
          {sending && <span style={{ marginLeft: 6 }}>Sending…</span>}
          {error && <span style={{ marginLeft: 6, color: "#b91c1c" }}>{error}</span>}
        </>
      )}
    </div>
  );
}
